import React from 'react';
import Grid from '@mui/material/Grid2';
import { ANALYTICS_EVENTS, ANALYTICS_EVENT_ACTIONS } from '../../utils/constants';
import { defaultDimensions } from '../../utils/analytics';
import { getUserId } from '../../utils/auth';
import Button from '@mui/material/Button';
import GetAppIcon from '@mui/icons-material/GetApp';
import Tooltip from '@mui/material/Tooltip';
import { useSnackBarContext } from '../../contexts/SnackbarContext';
import { GLOBAL_DOWNLOADS_GOOGLE_ANALYTICS_TAG } from './constants';
import makeStyles from '@mui/styles/makeStyles';
import { useGlobalDownloads } from './hooks/useGlobalDownloads';
import { useCallback, useState } from 'react';
import Loading from '../../components/Loading';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
  },
  globalDownloadsAppBar: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    zIndex: 1,
  },
  globalDownloadsHeader: {
    color: '#2a2e49',
    padding: '12px 36px',
    [theme.breakpoints.down('lg')]: {
      padding: '12px 10px',
    },
  },
  globalDownloadsDescription: {
    flexGrow: 1,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  globalDownloadsLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    overflow: 'auto',
  },
  button: {
    ...theme.palette.button,
    margin: theme.spacing(1),
    justifyContent: 'space-evenly',
  },
  buttonContainer: {
    ...theme.palette.mapInfoSection,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  label: {
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'center',
  },
}));

export const GlobalDownloadsPage = () => {
  const classes = useStyles();
  const { setSnack } = useSnackBarContext();
  const [downloading, setDownloading] = useState(false);

  const { downloadFieldSummaryByCompany } = useGlobalDownloads();

  const onDownload = useCallback(async () => {
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: GLOBAL_DOWNLOADS_GOOGLE_ANALYTICS_TAG,
      event_action: ANALYTICS_EVENT_ACTIONS.download,
      event_label: 'global_download_field_summary_by_company_excel',
      userId: getUserId(),
      ...defaultDimensions,
    });
    setDownloading(true);
    setSnack({
      open: true,
      message: 'Your download request has been submitted.',
      severity: 'info',
    });

    const { ok, errors } = await downloadFieldSummaryByCompany();

    if (!ok) {
      setSnack({
        open: true,
        message: errors[0]?.message || "We're sorry, but something went wrong.",
        severity: 'error',
      });
    } else {
      setSnack({
        open: true,
        message: 'Your download should begin shortly.',
        severity: 'success',
      });
    }
    setDownloading(false);
  }, [downloadFieldSummaryByCompany, setDownloading, setSnack]);

  return (
    <Grid container className={classes.root}>
      <AppBar className={classes.globalDownloadsAppBar} position="sticky">
        <Typography variant="body1" className={classes.globalDownloadsHeader}>
          GLOBAL DOWNLOADS
        </Typography>
      </AppBar>
      <Grid container direction="column" spacing={2} className={classes.globalDownloadsDescription}>
        <Grid>
          <Typography variant="body1" component="p">
            Access comprehensive Welligence data through a single Excel download. Our complete
            dataset export enables you to analyze, manipulate, and integrate the information into
            your existing workflows and systems beyond the Welligence platform.
          </Typography>
        </Grid>
        <Grid>
          <Tooltip title="Field Summary by Company" aria-label="field summary by company">
            {downloading && (
              <Grid className={classes.globalDownloadsLoading}>
                <Typography variant="body1" component="p">
                  Your global download can take approximately 5-10 minutes to generate. Please do
                  not close this window.
                </Typography>
                <Loading />
              </Grid>
            )}
            <Button
              className={classes.button}
              variant="contained"
              onClick={onDownload}
              disabled={downloading}
            >
              <GetAppIcon fontSize="small" color="primary" />
              <div className={classes.label}>Field Summary By Company</div>
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
};
